/**
 * API service for communicating with the Django backend.
 * 
 * In development: http://localhost:8000
 * In production: Configured via VITE_API_URL environment variable
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

interface TokenPair {
  access: string
  refresh: string
}

interface TokenRefreshResponse {
  access: string
  refresh?: string // Only present if ROTATE_REFRESH_TOKENS is True
}

interface ApiError {
  detail?: string
  message?: string
  [key: string]: unknown
}

class ApiService {
  private accessToken: string | null = null
  private refreshToken: string | null = null

  constructor() {
    // Load tokens from localStorage on init
    this.accessToken = localStorage.getItem('access_token')
    this.refreshToken = localStorage.getItem('refresh_token')
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    }

    if (this.accessToken && !endpoint.includes('/token/')) {
      headers['Authorization'] = `Bearer ${this.accessToken}`
    }

    const response = await fetch(url, {
      ...options,
      headers,
    })

    // Handle 401 - try to refresh token
    if (response.status === 401 && this.refreshToken && !endpoint.includes('/token/')) {
      const refreshed = await this.refreshAccessToken()
      if (refreshed) {
        // Retry the original request with new token
        headers['Authorization'] = `Bearer ${this.accessToken}`
        const retryResponse = await fetch(url, { ...options, headers })
        if (!retryResponse.ok) {
          const error = await retryResponse.json() as ApiError
          throw new Error(error.detail || error.message || 'Request failed')
        }
        return retryResponse.json()
      } else {
        // Refresh failed, clear tokens
        this.clearTokens()
        throw new Error('Session expired. Please sign in again.')
      }
    }

    if (!response.ok) {
      const error = await response.json() as ApiError
      throw new Error(error.detail || error.message || 'Request failed')
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T
    }

    return response.json()
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) return false

    try {
      const response = await fetch(`${API_BASE_URL}/api/token/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: this.refreshToken }),
      })

      if (!response.ok) {
        return false
      }

      const data: TokenRefreshResponse = await response.json()
      this.setTokens(data.access, data.refresh || this.refreshToken)
      return true
    } catch {
      return false
    }
  }

  setTokens(access: string, refresh: string) {
    this.accessToken = access
    this.refreshToken = refresh
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)
  }

  clearTokens() {
    this.accessToken = null
    this.refreshToken = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  hasTokens(): boolean {
    return !!this.accessToken && !!this.refreshToken
  }

  // Auth endpoints
  async obtainToken(email: string, password: string): Promise<TokenPair> {
    const data = await this.request<TokenPair>('/api/token/pair', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
    this.setTokens(data.access, data.refresh)
    return data
  }

  async verifyToken(): Promise<boolean> {
    if (!this.accessToken) return false
    
    try {
      await this.request('/api/token/verify', {
        method: 'POST',
        body: JSON.stringify({ token: this.accessToken }),
      })
      return true
    } catch {
      return false
    }
  }

  // User registration - This would need a custom endpoint in Django
  // For now, this is a placeholder that you'll implement on the backend
  async register(email: string, password: string, fullName: string): Promise<TokenPair> {
    const data = await this.request<TokenPair>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, full_name: fullName }),
    })
    this.setTokens(data.access, data.refresh)
    return data
  }

  // Generic request methods for future use
  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint)
  }

  post<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  put<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  patch<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }
}

// Export singleton instance
export const api = new ApiService()
export type { TokenPair, ApiError }

